import type { MortalityLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import { normalizeToIsoZMidnight } from '../../utils/dateNormalization'
import { createId } from '../../utils/id'
import { buildBiomassSamplingMortalityMetadata } from '../biomass/mortalityLogBridge'

export type MortalitySynthesisContext = {
  farmId: string
  productionUnitId: string
}

const MAX_MORTALITY_COUNT = 10_000_000

const parseMortalityValue = (value: unknown): number | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  if (!Number.isInteger(parsed)) return null
  if (parsed < 0 || parsed > MAX_MORTALITY_COUNT) return null
  return parsed
}

const trimmedOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

/**
 * Synthesize `MortalityLog` events from CSV-imported production rows.
 *
 * Used by the production-summary import path in `ImportWorkspace` so
 * imported `row.dailyMortality` values persist as authoritative MortalityLog
 * events consumed by `overlayMortalityLogsOnProductionRows`. Post-T3-A-07b
 * (2026-07-06) the paired `ProductionRecord.dailyMortality` cache column is
 * retired; without this synthesis the imported values live only in
 * localStorage and vanish on the first API refresh.
 *
 * Zero-count rows are intentionally skipped. Post-MORT-12 the semantic
 * meaning of "no MortalityLog events on (lot, day)" is "zero mortality
 * that day" (the overlay returns blank which the KPI/render layer treats
 * as zero). Emitting a `mortalityCount: 0` event would be indistinguishable
 * from an operator explicitly logging "0 died today" — a state the current
 * UI does not offer — and would inflate the event stream with no-op rows.
 *
 * Skip rules (produce no event for a row):
 * - `row.dailyMortality` is not a string, is blank, non-numeric, non-integer,
 *   negative, zero, or greater than 10,000,000.
 * - `row.date` cannot be normalized to an ISO Z instant.
 * - The synthesis context is missing `farmId` or `productionUnitId`.
 *
 * The caller is responsible for filtering `rows` down to *imported* rows
 * only (e.g. via `savePlan.syncRowIds`) — this helper does not distinguish
 * imported rows from pre-existing rows already in the table.
 */
export const synthesizeMortalityLogsFromImportedRows = (
  rows: BiomassDataRow[],
  context: MortalitySynthesisContext
): MortalityLog[] => {
  const farmId = typeof context.farmId === 'string' ? context.farmId.trim() : ''
  const productionUnitId = typeof context.productionUnitId === 'string'
    ? context.productionUnitId.trim()
    : ''
  if (!farmId || !productionUnitId) return []

  const importedAt = new Date().toISOString()
  const logs: MortalityLog[] = []

  for (const row of rows) {
    const mortalityCount = parseMortalityValue(row.dailyMortality)
    if (mortalityCount === null || mortalityCount === 0) continue

    const normalizedDate = normalizeToIsoZMidnight(typeof row.date === 'string' ? row.date : '')
    if (!normalizedDate.ok) continue

    const productionRowId = String(row.id)
    const lotId = trimmedOrUndefined(row.lotId)
    const stage: 'Active' | 'Inactive' = row.stage === 'Inactive' ? 'Inactive' : 'Active'

    logs.push({
      id: createId('mort'),
      farmId,
      productionUnitId,
      lot: trimmedOrUndefined(row.lot),
      recordedAt: normalizedDate.isoZ,
      mortalityCount: String(mortalityCount),
      notes: trimmedOrUndefined(row.notes),
      metadata: {
        ...buildBiomassSamplingMortalityMetadata({
          productionRowId,
          lotId: lotId ?? '',
          stage
        }),
        source: 'csvImport',
        productionRowId,
        lotId,
        importedAt
      }
    })
  }

  return logs
}
